import {formatCurrency} from "@lib/format";

interface SummaryDelta {
    before: string;
    after: string;
    delta: string;
}

interface CellDelta {
    cell: string;
    row_name: string;
    month_label: string;
    before: string;
    after: string;
    delta: string;
    delta_pct: string | null;
}

interface PreviewCardProps {
    summaryDeltas: Record<string, SummaryDelta>;
    deltas: CellDelta[];
    opsCount: number;
    domain?: "pnl" | "cashflow";
    onCommit?: () => void;
}

const PNL_SUMMARY_ROWS: {key: string; label: string; invert?: boolean}[] = [
    {key: "revenue", label: "Revenue"},
    {key: "cogs", label: "COGS", invert: true},
    {key: "grossProfit", label: "Gross Profit"},
    {key: "expenses", label: "Expenses", invert: true},
    {key: "netProfit", label: "Net Profit"},
];

const CASHFLOW_SUMMARY_ROWS: {key: string; label: string; invert?: boolean}[] = [
    {key: "totalInflow", label: "Total Inflow"},
    {key: "totalOutflow", label: "Total Outflow", invert: true},
    {key: "netMovement", label: "Net Movement"},
    {key: "closingBalance", label: "Closing Balance"},
];

function deltaColor(delta: number, invert = false): string {
    if (delta === 0) return "text-[#64748b]";
    const positive = invert ? delta < 0 : delta > 0;
    return positive ? "text-green-600" : "text-red-500";
}

function formatDelta(value: string): string {
    const n = parseFloat(value);
    if (n === 0) return "-";
    const sign = n > 0 ? "+" : "";
    return sign + formatCurrency(n, true);
}

export function PreviewCard({summaryDeltas, deltas, opsCount, domain = "pnl", onCommit}: PreviewCardProps) {
    const topDeltas = deltas.slice(0, 8);
    const SUMMARY_ROWS = domain === "cashflow" ? CASHFLOW_SUMMARY_ROWS : PNL_SUMMARY_ROWS;

    return (
        <div className="rounded-lg border-2 border-[#5BF6C8] bg-white p-4 space-y-3 my-2">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-[#1e293b]">
                    {domain === "cashflow" ? "Cash Flow" : "Forecast"} Preview
                </h4>
                <span className="text-xs text-[#64748b]">
                    {opsCount} op{opsCount !== 1 ? "s" : ""} previewed
                </span>
            </div>

            {/* Summary deltas table */}
            <table className="w-full text-xs">
                <thead>
                    <tr className="border-b border-gray-200">
                        <th className="text-left py-1 text-[#64748b] font-medium">Metric</th>
                        <th className="text-right py-1 text-[#64748b] font-medium">Before</th>
                        <th className="text-right py-1 text-[#64748b] font-medium">After</th>
                        <th className="text-right py-1 text-[#64748b] font-medium">Change</th>
                    </tr>
                </thead>
                <tbody>
                    {SUMMARY_ROWS.map(({key, label, invert}) => {
                        const d = summaryDeltas[key];
                        if (!d) return null;
                        const delta = parseFloat(d.delta);
                        return (
                            <tr key={key} className="border-b border-gray-50">
                                <td className="py-1.5 font-medium text-[#1e293b]">{label}</td>
                                <td className="text-right py-1.5 text-[#64748b]">
                                    {formatCurrency(parseFloat(d.before), true)}
                                </td>
                                <td className="text-right py-1.5 text-[#1e293b]">
                                    {formatCurrency(parseFloat(d.after), true)}
                                </td>
                                <td className={`text-right py-1.5 font-medium ${deltaColor(delta, invert)}`}>
                                    {formatDelta(d.delta)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Top affected line items */}
            {topDeltas.length > 0 && (
                <div>
                    <p className="text-xs text-[#64748b] font-medium mb-1">Top affected line items:</p>
                    <div className="space-y-0.5">
                        {topDeltas.map((d) => {
                            const delta = parseFloat(d.delta);
                            return (
                                <div key={d.cell} className="flex items-center justify-between text-xs">
                                    <span className="text-[#1e293b] truncate max-w-[50%]">
                                        {d.row_name || d.month_label || "Line item"}
                                        {d.month_label && (
                                            <span className="text-[#64748b] ml-1">({d.month_label})</span>
                                        )}
                                    </span>
                                    <span className={`font-medium ${deltaColor(delta)}`}>
                                        {formatDelta(d.delta)}
                                        {d.delta_pct && (
                                            <span className="text-[#64748b] ml-1">({d.delta_pct}%)</span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Submit button */}
            {onCommit && (
                <button
                    onClick={onCommit}
                    className="w-full mt-2 px-4 py-2 text-sm font-medium text-white bg-[#209DB3] rounded-lg hover:bg-[#1a8a9e] transition-colors"
                >
                    Submit Changes
                </button>
            )}
        </div>
    );
}
